import { c as _c } from "react/compiler-runtime";
import { Dialog, DialogPanel } from '@headlessui/react';
import type { Variants } from 'motion/react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import type { ReactNode, RefObject } from 'react';
import { Fragment, useState } from 'react';
import { cx } from '../../cx';
import { useMediaState } from '../../hooks/use-media-state';
import { Content } from './modal-content';
import { Footer } from './modal-footer';
import { CloseIcon, Header } from './modal-header';
import { Slides } from './slides/slides';
type ModalConfig = {
  activeContent?: string;
  setActiveContent: (name: string) => void;
  onClose: VoidFunction;
};
export type ModalProps = {
  isOpen: boolean;
  size?: 'small' | 'medium' | 'large' | 'full' | 'auto';
  height?: 'auto' | 'full';
  className?: string;
  initialContent?: string;
  scrollable?: boolean;
  /** Mobile: cover the whole screen (no gap at the top, square corners); unchanged from sm up. */
  fullScreenOnMobile?: boolean;
  initialFocusRef?: RefObject<HTMLInputElement>;
  children: ReactNode | ((config: ModalConfig) => ReactNode);
  onClose: VoidFunction;
};
const animationVariants: Record<string, Variants> = {
  backdrop: {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  },
  panel: {
    initial: {
      y: '100%'
    },
    animate: {
      y: 0
    },
    exit: {
      y: '100%'
    }
  }
};
const SIZES = {
  small: 'sm:max-w-112',
  medium: 'sm:max-w-144',
  large: 'sm:max-w-192',
  full: 'sm:max-w-full sm:w-full sm:h-full sm:max-h-full',
  auto: 'sm:w-auto'
};
const Root = t0 => {
  const $ = _c(18);
  const {
    isOpen,
    size,
    height: t1,
    className,
    initialContent,
    scrollable: t2,
    fullScreenOnMobile: t3,
    initialFocusRef,
    children,
    onClose
  } = t0;
  const height = t1 === undefined ? "auto" : t1;
  const scrollable = t2 === undefined ? false : t2;
  const fullScreenOnMobile = t3 === undefined ? false : t3;
  const isMobile = useMediaState("mobile");
  const [activeContent, setActiveContent] = useState(initialContent);
  let t4;
  if ($[0] !== initialContent) {
    t4 = () => setActiveContent(initialContent);
    $[0] = initialContent;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== activeContent || $[3] !== children || $[4] !== className || $[5] !== fullScreenOnMobile || $[6] !== height || $[7] !== initialContent || $[8] !== initialFocusRef || $[9] !== isMobile || $[10] !== isOpen || $[11] !== onClose || $[12] !== scrollable || $[13] !== size) {
    t5 = isOpen && <Dialog as={Fragment} onClose={onClose} initialFocus={initialFocusRef} open={isOpen} static={true}><div className={cx("fixed inset-0 z-modal flex h-full w-full", size !== "full" && "sm:fluid-grid items-end sm:place-content-center sm:p-12")}><m.div key="backdrop" onClick={onClose} className="fixed inset-0 z-backdrop bg-backdrop" initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.2
        }} variants={animationVariants.backdrop} /><DialogPanel as={Fragment}><m.div key="panel" initial="initial" animate="animate" exit="exit" transition={{
            duration: 0.2
          }} variants={isMobile ? animationVariants.panel : animationVariants.backdrop} className={cx("relative z-modal col-span-6 flex h-full w-screen flex-1 justify-self-center sm:col-span-12 sm:min-h-0 sm:w-full sm:max-w-192", size && SIZES[size], !initialContent && height === "auto" && "h-auto max-h-full", className)}><div className={cx("relative mt-8 flex max-h-full w-full flex-1 flex-col overflow-hidden rounded-t-3xl bg-white sm:mt-0 sm:rounded-3xl", size === "full" && "mt-0 rounded-t-none sm:rounded-none", fullScreenOnMobile && "mt-0 rounded-t-none", {
              "overflow-y-scroll": scrollable
            })}>{typeof children === "function" ? children({
                onClose,
                activeContent,
                setActiveContent
              }) : children}</div></m.div></DialogPanel></div></Dialog>;
    $[2] = activeContent;
    $[3] = children;
    $[4] = className;
    $[5] = fullScreenOnMobile;
    $[6] = height;
    $[7] = initialContent;
    $[8] = initialFocusRef;
    $[9] = isMobile;
    $[10] = isOpen;
    $[11] = onClose;
    $[12] = scrollable;
    $[13] = size;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <AnimatePresence onExitComplete={t4}>{t5}</AnimatePresence>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
export const Modal = Object.assign(Root, {
  Header,
  Content,
  Footer,
  Slides,
  CloseIcon
});